import { useState } from 'react';
import { type FinanceTransaction, currencySymbols, currentUser, cashAuditLogs } from '@/mocks/cash';

interface DeleteConfirmModalProps {
  tx: FinanceTransaction;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export default function DeleteConfirmModal({ tx, onConfirm, onCancel }: DeleteConfirmModalProps) {
  const [reason, setReason] = useState('');
  const [emailCopied, setEmailCopied] = useState(false);

  const accountLabel = tx.account === 'cash' ? 'Nakit Kasa' : 'Banka Hesabı';
  const sign = tx.type === 'income' ? '+' : '-';
  const amountStr = `${currencySymbols[tx.currency || 'TRY']}${tx.amount.toLocaleString('tr-TR')}`;
  const bankPart = tx.bankName ? ` (${tx.bankName})` : '';

  const emailBody = `Sistemimizden 1 adisyon silinmiştir. Detayları aşağıdadır.

Müşteri: ${tx.customerName || '—'}
Hizmet/Açıklama: ${tx.description}
Tutar: ${amountStr}
Ödeme Yöntemi: ${tx.paymentType}
Tarih/Saat: ${tx.date} ${tx.time}
İşlemi Yapan Kullanıcı: ${currentUser}

Silinen Kasa:
${accountLabel}${bankPart} ${sign}${amountStr}${reason ? `\n\nSilme Nedeni: ${reason}` : ''}`;

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(emailBody).then(() => {
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    });
  };

  const handleConfirm = () => {
    cashAuditLogs.unshift({
      id: `log-${Date.now()}`,
      action: 'delete',
      timestamp: new Date().toISOString(),
      performedBy: currentUser,
      txSnapshot: { ...tx },
      reason: reason.trim() || undefined,
    });
    onConfirm(reason);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onCancel} />
      <div
        className="relative w-full max-w-lg flex flex-col rounded-spa border overflow-hidden animate-scale-in max-h-[90vh]"
        style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: 'rgba(239,68,68,0.12)' }}>
              <i className="ri-delete-bin-line text-sm" style={{ color: '#EF4444' }} />
            </div>
            <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
              İşlemi Sil
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Transaction summary */}
          <div className="p-3 rounded-spa border" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-color)' }}>
            <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>Silinecek İşlem</p>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{tx.description}</p>
                {tx.customerName && <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{tx.customerName}</p>}
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{tx.date} {tx.time} · {tx.paymentType}</p>
              </div>
              <span
                className="text-sm font-bold font-display whitespace-nowrap"
                style={{ color: tx.type === 'income' ? '#10B981' : '#EF4444' }}
              >
                {sign}{amountStr}
              </span>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>
              Silme Nedeni <span className="font-normal" style={{ color: 'var(--text-muted)' }}>(opsiyonel)</span>
            </label>
            <input
              className="spa-input text-sm"
              placeholder="Örn. Yanlış ödeme yöntemi girildi, tekrar eklenecek..."
              value={reason}
              onChange={e => setReason(e.target.value)}
              maxLength={200}
            />
          </div>

          {/* Email preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                <i className="ri-mail-line mr-1" />
                Bildirim E-postası Önizlemesi
              </p>
              <button
                onClick={handleCopyEmail}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium cursor-pointer transition-all"
                style={{ background: emailCopied ? 'rgba(16,185,129,0.12)' : 'var(--bg-tertiary)', color: emailCopied ? '#10B981' : 'var(--text-secondary)' }}
              >
                <i className={emailCopied ? 'ri-check-line' : 'ri-file-copy-line'} />
                {emailCopied ? 'Kopyalandı!' : 'Kopyala'}
              </button>
            </div>
            <div
              className="p-3 rounded-spa text-xs font-mono whitespace-pre-wrap leading-5"
              style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
            >
              {emailBody}
            </div>
          </div>

          <div className="p-3 rounded-spa flex items-start gap-2" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
              <i className="ri-error-warning-line text-xs" style={{ color: '#EF4444' }} />
            </div>
            <p className="text-xs" style={{ color: '#EF4444' }}>
              Bu işlem audit loguna kaydedilecek ve geri alınamaz. Devam etmek istediğinizden emin misiniz?
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
          <button onClick={onCancel} className="spa-btn-secondary text-sm">İptal</button>
          <button
            onClick={handleConfirm}
            className="flex items-center gap-1.5 px-4 py-2 rounded-spa-sm text-sm font-medium text-white cursor-pointer whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #EF4444, #DC2626)' }}
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-delete-bin-line text-xs" /></div>
            Sil ve Logla
          </button>
        </div>
      </div>
    </div>
  );
}